import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy,
  TrendingUp,
  Sparkles,
  Mail,
  ShieldAlert,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
} from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { key: "health_score", label: "Health score", icon: Activity },
  { key: "completion", label: "Completion DVR", icon: CheckCircle2 },
  { key: "ai_acceptance", label: "AI acceptance", icon: Sparkles },
  { key: "newsletter", label: "Newsletter", icon: Mail },
];

function healthTone(score) {
  if (score >= 75) return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (score >= 50) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-red-100 text-red-700 border-red-200";
}

export default function CustomerLeaderboard() {
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("health_score");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    apiRequest(`/analytics/customer-leaderboard?sort_by=${sort}`)
      .then((r) => {
        setData(r);
        setError("");
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [sort]);

  const top3 = useMemo(() => (data?.rows || []).slice(0, 3), [data]);

  return (
    <div className="space-y-6" data-testid="customer-leaderboard-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">
          <Trophy className="mr-1 inline h-3 w-3" /> Salute portafoglio clienti
        </p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">
          Leaderboard aziende
        </h2>
        <p className="mt-2 text-slate-600">
          Health score composito (0-100) basato su completamento DVR (40%),
          accettazione AI (20%), gestione scadenze (20%), engagement newsletter
          (10%) e firme dei DVR (10%).
        </p>
      </section>

      {data && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="leaderboard-totals">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Aziende</p>
              <p className="mt-1 text-2xl font-bold">{data.totals.companies}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Health medio</p>
              <p className="mt-1 text-2xl font-bold">{data.totals.avg_health}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Completion media</p>
              <p className="mt-1 text-2xl font-bold">{data.totals.avg_completion}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Rischi alti totali</p>
              <p className="mt-1 text-2xl font-bold text-red-700">{data.totals.total_high_risks}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="leaderboard-sort-bar">
        <span className="text-xs font-semibold uppercase text-slate-500">Ordina per:</span>
        {SORT_OPTIONS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSort(key)}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
              sort === key
                ? "bg-blue-900 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
            data-testid={`leaderboard-sort-${key}`}
          >
            <Icon className="h-3 w-3" /> {label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2" data-testid="leaderboard-export-actions">
          <Button variant="outline" size="sm" onClick={() => exportLeaderboard("csv")} data-testid="leaderboard-export-csv-button">
            <Download className="h-3.5 w-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportLeaderboard("pdf")} data-testid="leaderboard-export-pdf-button">
            <FileText className="h-3.5 w-3.5" /> PDF
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="leaderboard-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {top3.length > 0 && (
        <div className="grid gap-4 md:grid-cols-3" data-testid="leaderboard-top3">
          {top3.map((row, idx) => (
            <Card
              key={row.company_id}
              className={`shadow-md ${
                idx === 0 ? "border-amber-300" : idx === 1 ? "border-slate-300" : "border-orange-200"
              }`}
              data-testid={`leaderboard-top-${idx + 1}`}
            >
              <CardHeader>
                <p className="text-xs font-semibold uppercase text-slate-500">
                  <Trophy className={`mr-1 inline h-3 w-3 ${idx === 0 ? "text-amber-500" : idx === 1 ? "text-slate-400" : "text-orange-400"}`} />
                  #{idx + 1}
                </p>
                <CardTitle className="font-heading text-lg">
                  <Link to={`/aziende/${row.company_id}`} className="hover:underline">
                    {row.business_name}
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge className={healthTone(row.health_score)} data-testid={`leaderboard-health-${row.company_id}`}>
                  Health {row.health_score}
                </Badge>
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  <div>Completion: <strong>{row.completion_percentage}%</strong></div>
                  <div>AI acceptance: <strong>{row.ai_acceptance_rate}%</strong> ({row.ai_accepted}/{row.ai_total})</div>
                  <div>Newsletter inviate: <strong>{row.newsletter_count}</strong></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="shadow-none">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Tutte le aziende</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="p-6 text-center text-sm text-slate-500">Caricamento…</p>
          ) : !data?.rows?.length ? (
            <p className="p-6 text-center text-sm text-slate-500" data-testid="leaderboard-empty">
              Nessuna azienda nel tuo portafoglio.
            </p>
          ) : (
            <table className="w-full min-w-[800px] text-sm" data-testid="leaderboard-table">
              <thead className="border-b text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-3">Azienda</th>
                  <th className="p-3">Health</th>
                  <th className="p-3">DVR %</th>
                  <th className="p-3">Rischi alti</th>
                  <th className="p-3">Scadenze fuori</th>
                  <th className="p-3">AI accept</th>
                  <th className="p-3">Newsletter</th>
                  <th className="p-3">Revisioni firmate</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.company_id} className="border-b last:border-b-0" data-testid={`leaderboard-row-${row.company_id}`}>
                    <td className="p-3">
                      <Link to={`/aziende/${row.company_id}`} className="font-semibold text-slate-950 hover:underline">
                        {row.business_name}
                      </Link>
                      <p className="text-xs text-slate-500">ATECO {row.ateco_code || "—"}</p>
                    </td>
                    <td className="p-3">
                      <Badge className={healthTone(row.health_score)}>{row.health_score}</Badge>
                    </td>
                    <td className="p-3 font-semibold">{row.completion_percentage}%</td>
                    <td className="p-3">
                      {row.high_risks_count > 0 ? (
                        <span className="font-semibold text-red-700">
                          <ShieldAlert className="mr-1 inline h-3 w-3" />
                          {row.high_risks_count}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="p-3">
                      {row.actions_overdue > 0 ? (
                        <span className="font-semibold text-amber-700">{row.actions_overdue}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-xs">
                        {row.ai_acceptance_rate}% ({row.ai_accepted}/{row.ai_total})
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs">{row.newsletter_count}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-xs">
                        {row.signed_revisions}/{row.total_revisions || 0}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
